/**
 * AISpecialistSection — shared "Can Delegate To" orchestration UI.
 *
 * Used by both TransferToAIEditor and NetsuiteAIEditor (and any future AI specialist nodes
 * like ShopifyAI, BigCommerceAI, etc.) so the delegation logic is defined once.
 *
 * How it works:
 *   The primary AI's prompt is injected with a tool for each specialist:
 *     delegate_to_{snake_case_name}(reason: string)
 *   The "Specialist handles" field defines the scope the AI uses to decide when to delegate.
 *   Each specialist runs with its own AI config + its own MCP tool servers.
 *   When done, the specialist calls return_to_previous — control returns to the primary AI.
 */
import React, { useState } from "react";
import Select from "@/components/Select";
import { WorkflowNode, ConnectedAiConfig } from "../../types";
import { AIConfiguration } from "@/pages/Messaging/Bots/types";
import { McpServerConfig } from "@/hooks/useMcpServers";
import McpStaleBadge from "./McpStaleBadge";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
  aiConfigurations: AIConfiguration[];
  enabledMcpServers: McpServerConfig[];
}

/**
 * Given a list of snake_case tool names, produces a human-readable scope suggestion.
 * e.g. ["search_orders","get_order_status","track_shipment"] → "search orders, order status, track shipment"
 */
function suggestScopeFromTools(toolNames: string[]): string {
  if (!toolNames || toolNames.length === 0) return "";
  const humanized = toolNames.map((t) =>
    t
      .replace(/_/g, " ")
      .replace(/^(get|fetch|list|create|update|delete|find|search|check|lookup)\s/i, "")
      .trim()
  );
  // Deduplicate while preserving order
  const seen = new Set<string>();
  const unique = humanized.filter((s) => {
    if (seen.has(s)) return false;
    seen.add(s);
    return true;
  });
  return unique.slice(0, 6).join(", ");
}

export default function AISpecialistSection({
  node,
  onUpdate,
  aiConfigurations,
  enabledMcpServers
}: Props) {
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());

  const connectedAiConfigs: ConnectedAiConfig[] = Array.isArray(node.connectedAiConfigs)
    ? node.connectedAiConfigs
    : [];

  // Exclude self and already-added specialists
  const availableToAdd = aiConfigurations.filter(
    (ai) =>
      ai.id !== node.aiConfigId &&
      !connectedAiConfigs.some((c) => c.aiConfigId === ai.id)
  );

  function addDelegate(aiConfigId: number) {
    const ai = aiConfigurations.find((a) => a.id === aiConfigId);
    if (!ai) return;
    if (connectedAiConfigs.some((c) => c.aiConfigId === aiConfigId)) return;
    const next: ConnectedAiConfig[] = [
      ...connectedAiConfigs,
      { aiConfigId, name: ai.name, when: "", mcpServerIds: [] }
    ];
    onUpdate({ connectedAiConfigs: next });
  }

  function removeDelegate(aiConfigId: number) {
    const next = connectedAiConfigs.filter((c) => c.aiConfigId !== aiConfigId);
    onUpdate({ connectedAiConfigs: next.length > 0 ? next : undefined });
  }

  function updateDelegateWhen(aiConfigId: number, when: string) {
    const next = connectedAiConfigs.map((c) =>
      c.aiConfigId === aiConfigId ? { ...c, when } : c
    );
    onUpdate({ connectedAiConfigs: next });
  }

  function updateDelegationInstructions(aiConfigId: number, delegationInstructions: string) {
    const next = connectedAiConfigs.map((c) =>
      c.aiConfigId === aiConfigId ? { ...c, delegationInstructions } : c
    );
    onUpdate({ connectedAiConfigs: next });
  }

  function toggleDelegateMcp(aiConfigId: number, mcpId: number, checked: boolean) {
    const next = connectedAiConfigs.map((c) => {
      if (c.aiConfigId !== aiConfigId) return c;
      const ids = Array.isArray(c.mcpServerIds) ? c.mcpServerIds : [];
      const updated = checked ? [...ids, mcpId] : ids.filter((x) => x !== mcpId);

      // Auto-suggest scope from tool names when 'when' is empty and an MCP is being checked
      let when = c.when;
      if (checked && !when?.trim()) {
        const allToolNames = enabledMcpServers
          .filter((s) => updated.includes(s.id))
          .flatMap((s) => s.toolNames ?? []);
        const suggestion = suggestScopeFromTools(allToolNames);
        if (suggestion) when = suggestion;
      }

      return { ...c, mcpServerIds: updated, when };
    });
    onUpdate({ connectedAiConfigs: next });
  }

  function togglePromptExpanded(aiConfigId: number) {
    setExpandedPrompts((prev) => {
      const next = new Set(prev);
      if (next.has(aiConfigId)) next.delete(aiConfigId);
      else next.add(aiConfigId);
      return next;
    });
  }

  if (aiConfigurations.length <= 1) {
    // Need at least 2 AI configs to delegate
    return null;
  }

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Specialist Agents
        </span>
        <span className="text-xs text-slate-400 italic">optional</span>
      </div>
      <p className="text-xs text-slate-500 mb-3 leading-relaxed">
        The primary AI can automatically hand off to a specialist when the conversation matches
        their scope, then resume when they're done.{" "}
        <a href="/bots" className="text-blue-600 hover:underline">
          Manage AI Configs →
        </a>
      </p>

      {connectedAiConfigs.length > 0 && (
        <div className="flex flex-col gap-3 mb-3">
          {connectedAiConfigs.map((delegate) => {
            const ai = aiConfigurations.find((a) => a.id === delegate.aiConfigId);
            const promptPreview = ai?.prompt?.trim();
            const isExpanded = expandedPrompts.has(delegate.aiConfigId);

            return (
              <div
                key={delegate.aiConfigId}
                className="border border-blue-200 bg-blue-50 rounded-lg p-3 flex flex-col gap-2.5"
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                    <span className="text-sm font-semibold text-blue-900 truncate">
                      {ai?.name ?? `AI Config #${delegate.aiConfigId}`}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDelegate(delegate.aiConfigId)}
                    className="text-xs text-slate-400 hover:text-red-500 transition-colors shrink-0"
                    aria-label={`Remove ${ai?.name ?? "specialist"} from delegates`}
                  >
                    Remove
                  </button>
                </div>

                {/* Prompt preview */}
                {promptPreview ? (
                  <div>
                    <button
                      type="button"
                      onClick={() => togglePromptExpanded(delegate.aiConfigId)}
                      className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                    >
                      <span>{isExpanded ? "▼" : "▶"}</span>
                      <span>System prompt preview</span>
                    </button>
                    {isExpanded && (
                      <p className="mt-1 text-xs text-slate-600 bg-white border border-slate-200 rounded p-2 leading-relaxed line-clamp-4 font-mono">
                        {promptPreview.slice(0, 300)}
                        {promptPreview.length > 300 ? "…" : ""}
                      </p>
                    )}
                    <a
                      href="/bots"
                      className="text-xs text-blue-600 hover:underline mt-1 inline-block"
                    >
                      Edit this specialist's prompt →
                    </a>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      No system prompt set — this specialist has no instructions.{" "}
                      <a href="/bots" className="underline">
                        Add a prompt →
                      </a>
                    </p>
                  </div>
                )}

                {/* Specialist scope / "when" field */}
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-slate-700">
                    Specialist handles topics like…
                  </span>
                  <span className="text-xs text-slate-500">
                    The primary AI uses this to decide when to delegate here.
                  </span>
                  <input
                    type="text"
                    className="input text-xs mt-1"
                    placeholder='e.g. "orders, shipping, returns, tracking"'
                    value={delegate.when ?? ""}
                    onChange={(e) => updateDelegateWhen(delegate.aiConfigId, e.target.value)}
                  />
                </label>

                {/* Delegation instructions — injected as handoff context when primary AI delegates */}
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-slate-700">
                    Delegation instructions
                  </span>
                  <span className="text-xs text-slate-500 leading-relaxed">
                    Optional "handoff note" injected when control passes to this specialist. Use it
                    to tell the specialist what to do first, what to ask, or what context to keep
                    in mind. This is separate from its system prompt — it only applies when
                    delegated from this node.
                  </span>
                  <textarea
                    className="input text-xs mt-1 resize-y min-h-[60px]"
                    placeholder={
                      'e.g. "Ask the customer to confirm their order number before searching. If they seem frustrated, acknowledge the delay and offer to escalate."'
                    }
                    value={delegate.delegationInstructions ?? ""}
                    onChange={(e) =>
                      updateDelegationInstructions(delegate.aiConfigId, e.target.value)
                    }
                    aria-label={`Delegation instructions for ${ai?.name ?? "specialist"}`}
                  />
                </label>

                {/* Per-specialist MCP servers */}
                {enabledMcpServers.length > 0 && (
                  <div>
                    <span className="text-xs font-medium text-slate-700 block mb-1">
                      Tools available to this specialist
                    </span>
                    <div className="flex flex-col gap-1">
                      {enabledMcpServers.map((server) => {
                        const checked = (delegate.mcpServerIds ?? []).includes(server.id);
                        return (
                          <label
                            key={server.id}
                            className="flex items-center gap-2 cursor-pointer group flex-wrap"
                          >
                            <input
                              type="checkbox"
                              className="w-3.5 h-3.5 shrink-0"
                              checked={checked}
                              onChange={(e) =>
                                toggleDelegateMcp(
                                  delegate.aiConfigId,
                                  server.id,
                                  e.target.checked
                                )
                              }
                              aria-label={`Enable ${server.name} for ${ai?.name ?? "specialist"}`}
                            />
                            <span className="text-xs text-slate-700 group-hover:text-slate-900">
                              {server.name}
                            </span>
                            {server.toolCount > 0 && (
                              <span className="text-xs text-slate-400">
                                ({server.toolCount} tools)
                              </span>
                            )}
                            <McpStaleBadge lastDiscoveredAt={server.lastDiscoveredAt} />
                          </label>
                        );
                      })}
                    </div>

                    {/* Re-suggest scope button — shown when MCPs are checked */}
                    {(delegate.mcpServerIds ?? []).length > 0 && (
                      <button
                        type="button"
                        className="mt-1.5 text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        onClick={() => {
                          const allToolNames = enabledMcpServers
                            .filter((s) => (delegate.mcpServerIds ?? []).includes(s.id))
                            .flatMap((s) => s.toolNames ?? []);
                          const suggestion = suggestScopeFromTools(allToolNames);
                          if (suggestion) updateDelegateWhen(delegate.aiConfigId, suggestion);
                        }}
                        title="Auto-fill scope from tool names"
                      >
                        ↺ Suggest scope from tool names
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {availableToAdd.length > 0 && (
        <Select
          options={[
            { value: 0, label: "— Add specialist agent —" },
            ...availableToAdd.map((ai) => ({ value: ai.id, label: ai.name }))
          ]}
          value={0}
          onChange={(value) => {
            if (Number(value) > 0) addDelegate(Number(value));
          }}
        />
      )}

      {availableToAdd.length === 0 && connectedAiConfigs.length > 0 && (
        <p className="text-xs text-slate-400 italic">All available AI configs are added.</p>
      )}
    </div>
  );
}
